/**
 * The allocation matrix: items down, suppliers across (prototype `vCompare`'s `.wb`/`.wbt`).
 *
 * Built to scale — real enquiries run to hundreds of items across many suppliers — so the
 * item column and the summary column are sticky while the supplier columns scroll
 * horizontally, and the header sticks on vertical scroll.
 *
 * Every number shown is derived in `lib/compare.ts`; this file only renders. Rows are keyed by
 * `(item_code, enquiry_row_no)` — a customer who asked for the same item on two enquiry lines
 * gets two independent rows here (Task 10: `comparison.get_comparison` sends one row per
 * enquiry line, not per item code), so `item_code` alone is not a safe React key.
 */

import { Badge } from '../../components/Badge'
import { Money } from '../../components/Money'
import { OfferCell } from './OfferCell'
import { formatMoney } from '../../lib/format'
import { bestUnitPrice, isBestOffer, rowsForItem, summariseItem, type NormalisedItem } from '../../lib/compare'
import type { AllocationRow, Currency } from '../../lib/types'
import type { DisplayCurrency } from '../../lib/displayCurrency'

export function WorkbenchTable({
  items,
  visibleSuppliers,
  supplierName,
  allocations,
  baseCurrency,
  display,
  onToggleCell,
  onSetQty,
  onAutoCover,
  onPriceLine,
  onUseOurStock,
  inHouseFor,
}: {
  items: NormalisedItem[]
  /** `Supplier.code` values — see `ComparisonSupplier.supplier`. */
  visibleSuppliers: string[]
  /** Code → display name, for rendering only; every key/comparison stays on the code. */
  supplierName: Map<string, string>
  allocations: AllocationRow[]
  baseCurrency: Currency
  /** The currency the customer is quoted in; figures are shown in it beside the base ones. */
  display?: DisplayCurrency
  onToggleCell: (itemCode: string, enquiryRowNo: string, supplier: string) => void
  onSetQty: (itemCode: string, enquiryRowNo: string, supplier: string, qty: number) => void
  onAutoCover: (itemCode: string, enquiryRowNo: string) => void
  /**
   * Prices the row from our own stock directly — the same write the dialog's "Use our stock"
   * makes, without the dialog (Akhil, 2026-09-18: "like we did in the RFQ builder"). The badge
   * is the offer; clicking it takes it.
   */
  onUseOurStock?: (rowNo: string) => void
  /** Opens the price-this-line dialog — our stock, contracts, past supplier prices — for a row. */
  onPriceLine?: (enquiryRowNo: string) => void
  /**
   * Our own price for the row's unit and what is free on the shelf — shown as a tag under the
   * item (Akhil, 2026-09-15) so the in-house option is visible beside the offers without opening
   * the dialog. `null` price means the catalogue has none for this unit.
   */
  inHouseFor?: (enquiryRowNo: string) => { price: { amount: number; currency: string; uom: string; buying: boolean } | null; available?: number }
}) {
  return (
    <div className="wb">
      <table className="wbt">
        <thead>
          <tr>
            <th className="sticky">Item</th>
            <th className="right">Qty</th>
            {visibleSuppliers.map((supplier) => (
              <th key={supplier} className="right">
                {supplierName.get(supplier) ?? supplier}
              </th>
            ))}
            <th className="sum right">Allocation</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={visibleSuppliers.length + 3} style={{ color: 'var(--muted)', padding: 22, textAlign: 'center' }}>
                No items match the filter.
              </td>
            </tr>
          ) : null}

          {items.map((item) => {
            const allocation = rowsForItem(allocations, item)
            const summary = summariseItem(item, allocation)
            // Ranked among the suppliers actually shown: hiding the cheapest promotes the
            // next one to "best" rather than leaving the row unhighlighted (prototype 863).
            const best = bestUnitPrice(item, visibleSuppliers)

            return (
              <tr key={`${item.item_code}::${item.enquiry_row_no}`}>
                <td className="sticky">
                  <div style={{ fontWeight: 700, whiteSpace: 'normal' }}>{item.name}</div>
                  <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>
                    {item.item_code}
                    {onPriceLine ? (
                      <>
                        {' · '}
                        {/* The alternatives to the fresh offers, in one dialog (Akhil, 2026-09-15). */}
                        <span
                          className="linkish"
                          onClick={() => onPriceLine(item.enquiry_row_no)}
                          title="Price this line from our own stock, a contract, or a past supplier price — instead of one of the offers here"
                        >
                          price this line
                        </span>
                      </>
                    ) : null}
                  </div>

                  {/* Our own stock, as a tag — the option the offers are competing with. */}
                  {(() => {
                    const own = inHouseFor?.(item.enquiry_row_no)
                    if (!own || !own.price) return null
                    const stock = own.available
                    return (
                      <div style={{ marginTop: 3 }}>
                        <span
                          className="pill"
                          role={onUseOurStock ? 'button' : undefined}
                          tabIndex={onUseOurStock ? 0 : undefined}
                          style={{ background: 'var(--teal)', color: '#fff', cursor: onUseOurStock || onPriceLine ? 'pointer' : undefined }}
                          title={`${own.price.buying ? 'In-house cost' : 'Our SELLING price, not a cost'} per ${own.price.uom}${
                            stock !== undefined ? ` · ${stock} ${item.uom} free to commit` : ''
                          }. Click to price this line from our stock at this rate — it leaves the comparison and goes on the quotation.`}
                          // One click prices it (2026-09-18); the dialog stays reachable via "price this line".
                          onClick={() => (onUseOurStock ?? onPriceLine)?.(item.enquiry_row_no)}
                          onKeyDown={(e) => {
                            if (onUseOurStock && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault()
                              onUseOurStock(item.enquiry_row_no)
                            }
                          }}
                        >
                          our stock {formatMoney({ amount: own.price.amount, currency: own.price.currency as Currency })}/{own.price.uom}
                          {stock !== undefined ? ` · ${stock} on hand` : ''}
                        </span>
                      </div>
                    )
                  })()}

                  {/* What the customer actually wrote. The item name is our vocabulary; this
                      is theirs, and it is what a doubtful allocation gets checked against. */}
                  {item.lines.map((line) => (
                    <div
                      key={line.line_id}
                      style={{ marginTop: 3, whiteSpace: 'pre-line', fontSize: 11.5, color: 'var(--muted)' }}
                    >
                      <span className="pill" title="Customer's own serial number">
                        #{line.cust_sno}
                      </span>{' '}
                      {line.description}
                      {line.edited ? (
                        <div style={{ color: 'var(--ink)', fontSize: 11.5, whiteSpace: 'pre-line' }} title="The description as edited on the RFQ builder — prints on the quotation">
                          ✎ {line.edited}
                        </div>
                      ) : null}
                    </div>
                  ))}

                  {/* A supplier dropped for an unconvertible UOM or currency must be visible, not silently gone. */}
                  {item.excluded.map((excluded) => (
                    <div key={excluded.supplier} style={{ marginTop: 3, whiteSpace: 'normal' }}>
                      <Badge tone="danger" title={excluded.reason}>
                        {supplierName.get(excluded.supplier) ?? excluded.supplier} excluded
                      </Badge>
                      <div style={{ color: 'var(--muted)', fontSize: 10.5 }}>
                        {/* Their price is still shown — withholding it just sends the
                            purchaser back to the supplier screen to look it up. */}
                        quoted <Money value={excluded.raw.rate} />/{excluded.raw.sq_uom} · {excluded.reason}
                      </div>
                    </div>
                  ))}
                </td>

                <td className="right mono">
                  {item.qty}
                  {/* The unit every offer on this row is ranked per — "10" alone is not a
                      quantity when suppliers quote in drums and pieces. */}
                  <div style={{ color: 'var(--muted)', fontSize: 11 }}>{item.uom}</div>
                </td>

                {visibleSuppliers.map((supplier) => {
                  const offer = item.bySupplier.get(supplier)
                  return (
                    <OfferCell
                      key={supplier}
                      offer={offer}
                      requiredQty={item.qty}
                      allocatedQty={allocation.find((row) => row.supplier === supplier)?.allocated_qty ?? 0}
                      isBest={!!offer && isBestOffer(offer, best)}
                      baseCurrency={baseCurrency}
                      display={display}
                      onToggle={() => onToggleCell(item.item_code, item.enquiry_row_no, supplier)}
                      onSetQty={(qty) => onSetQty(item.item_code, item.enquiry_row_no, supplier, qty)}
                    />
                  )
                })}

                <td className="sum">
                  <div className="allocsum">
                    <div className="lab">
                      {summary.allocated}/{item.qty}{' '}
                      {summary.remaining === 0 ? (
                        <Badge tone="ok">covered</Badge>
                      ) : summary.remaining > 0 ? (
                        <Badge tone="warn">{summary.remaining} left</Badge>
                      ) : (
                        <Badge tone="danger">{-summary.remaining} over</Badge>
                      )}
                      {summary.supplierCount > 1 ? (
                        <>
                          {' · '}
                          <Badge tone="violet">{summary.supplierCount} suppliers</Badge>
                        </>
                      ) : null}
                    </div>
                    {/*
                      The chosen supplier(s) and what they SAID, here in the pinned column
                      (Akhil, 2026-09-15): on a hundred-line enquiry the offer's remark sits in a
                      cell somewhere off to the left, and scrolling out to read "subject to
                      availability" on every line is the cost this saves. Only allocated offers —
                      the decision, not the whole matrix.
                    */}
                    {allocation
                      .filter((row) => row.allocated_qty > 0)
                      .map((row) => {
                        const offer = item.bySupplier.get(row.supplier)
                        return (
                          <div key={row.supplier} style={{ fontSize: 10.5, whiteSpace: 'normal', marginTop: 2 }}>
                            <span style={{ fontWeight: 700 }}>{supplierName.get(row.supplier) ?? row.supplier}</span>
                            {allocation.length > 1 ? ` · ${row.allocated_qty}` : ''}
                            {offer?.raw.remark ? (
                              <span style={{ color: 'var(--teal)', fontStyle: 'italic' }}> “{offer.raw.remark}”</span>
                            ) : null}
                          </div>
                        )
                      })}
                    <div className="lab">
                      line <Money value={{ amount: summary.cost, currency: baseCurrency }} in={display?.currency} fx={display?.fx} /> ·{' '}
                      <span
                        style={{ cursor: 'pointer', color: 'var(--brand)', fontWeight: 700 }}
                        onClick={() => onAutoCover(item.item_code, item.enquiry_row_no)}
                      >
                        auto-cover
                      </span>
                    </div>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
